/**
 * Leave a park (Intent Approve, Plan Build, question / runtime HITL submit).
 * Multiplex-only: ignore *stale* park frames of the card you just left until the
 * next run-start — do not swallow the next park (plan after intent, HITL after build).
 * Busy / Working… is owned by builder.setBusy (FlowOps isGenerating).
 */
import { flushSync } from "react-dom";

import { abortActiveWorkflowStream } from "@/shared/builder-session";
import type { StructuredIntent } from "@/shared/builder-session";
import { setChatLive } from "@/features/chat/multi/history/chat-history";
import { setChatAwaitingInput } from "@/features/chat/multi/signals/chat-signals";
import type { BuilderContextValue } from "./builder-types";
import { isBenignHitlResumeFailure, serviceError } from "./sse/chat-helpers";

type LeaveReviewKind = "intent" | "plan" | "hitl";

/** Multiplex: which park surface was just left (stale frames of that surface only). */
const ignoreLateReviewPark = new Map<string, LeaveReviewKind>();

/** Yellow off + live on for sidebar; does not touch builder.busy. */
export function markWorkingAfterReview(sessionId: string, kind: LeaveReviewKind = "hitl"): void {
  const id = sessionId.trim();
  if (!id) return;
  ignoreLateReviewPark.set(id, kind);
  setChatAwaitingInput(id, false);
  setChatLive(id, true);
}

export function clearWorkingAfterReview(sessionId: string | null | undefined): void {
  const id = sessionId?.trim();
  if (!id) return;
  ignoreLateReviewPark.delete(id);
}

/**
 * Ignore only stale re-parks of the card that was just left.
 * Intent Approve → still allow plan / HITL parks (sidebar yellow for Build / questions).
 * Plan Build → ignore late plan/awaiting-approval only.
 * HITL submit → ignore all parks until run-start.
 */
export function shouldIgnoreLateReviewPark(
  sessionId: string | null | undefined,
  event?: string,
): boolean {
  const id = sessionId?.trim();
  if (!id) return false;
  const kind = ignoreLateReviewPark.get(id);
  if (!kind) return false;
  if (!event) return true;
  if (kind === "intent") {
    return event === "awaiting-intent-approval";
  }
  if (kind === "plan") {
    return event === "plan" || event === "awaiting-approval";
  }
  return true;
}

export function confirmIntentApproval(options: {
  builder: BuilderContextValue;
  confirming: boolean;
  setConfirming: (value: boolean) => void;
  stream: (url: string, body: Record<string, unknown>) => Promise<void>;
}): void {
  const { builder, confirming, setConfirming, stream } = options;
  if (!builder.sessionId || !builder.intent || confirming) return;
  if (builder.intent.status === "confirmed") return;
  if (builder.intent.intent.unknowns.length > 0) {
    builder.setError("Resolve the remaining questions before approving intent.");
    return;
  }

  const sessionId = builder.sessionId;
  const pendingIntent = builder.intent;
  const toolCallId = pendingIntent.id.startsWith("pending-")
    ? pendingIntent.id.slice("pending-".length)
    : pendingIntent.id;

  if (builder.busy) abortActiveWorkflowStream(sessionId);
  builder.setError(null);

  const confirmed: StructuredIntent = {
    ...pendingIntent,
    status: "confirmed",
    confirmedAt: new Date().toISOString(),
  };

  setConfirming(true);
  flushSync(() => {
    builder.setIntent(confirmed);
    builder.restoreMessages(
      builder.messages.map((message) =>
        message.type === "intent" &&
        (message.details as StructuredIntent | undefined)?.id === pendingIntent.id
          ? { ...message, details: confirmed }
          : message,
      ),
    );
    builder.setPhase("planning");
    builder.setBusy(true);
  });
  markWorkingAfterReview(sessionId, "intent");
  // Approved is painted; do not leave the button on Confirming… for the whole run.
  queueMicrotask(() => setConfirming(false));

  void stream("/api/agent-runtime/tool-approval", {
    sessionId,
    toolCallId,
    decision: "approve",
    intentDraft: pendingIntent.intent,
  }).catch((error) => {
    setConfirming(false);
    clearWorkingAfterReview(sessionId);
    builder.setBusy(false);
    setChatLive(sessionId, false);
    if (isBenignHitlResumeFailure(error)) {
      builder.setError(null);
      return;
    }
    setChatAwaitingInput(sessionId, true);
    builder.setIntent(pendingIntent);
    builder.restoreMessages(
      builder.messages.map((message) =>
        message.type === "intent" &&
        (message.details as StructuredIntent | undefined)?.id === confirmed.id
          ? { ...message, details: pendingIntent }
          : message,
      ),
    );
    builder.setPhase("awaiting intent approval");
    builder.setError(serviceError(error));
  });
}

export function approvePlanBuild(options: {
  builder: BuilderContextValue;
  overrideMissingConnections: boolean;
  stream: (url: string, body: Record<string, unknown>) => Promise<void>;
}): void {
  const { builder, overrideMissingConnections, stream } = options;
  if (!builder.sessionId || builder.phase !== "awaiting approval" || builder.nodes.length > 0) {
    return;
  }

  const sessionId = builder.sessionId;
  if (builder.busy) abortActiveWorkflowStream(sessionId);

  flushSync(() => {
    builder.prepareBuild();
    builder.setPhase("building");
    builder.setBusy(true);
  });
  markWorkingAfterReview(sessionId, "plan");

  void stream("/api/workflow/continue", {
    sessionId,
    overrideMissingConnections,
  }).catch((error) => {
    clearWorkingAfterReview(sessionId);
    builder.setBusy(false);
    setChatLive(sessionId, false);
    setChatAwaitingInput(sessionId, true);
    builder.setPhase("awaiting approval");
    builder.setError(serviceError(error));
  });
}
